/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useCallback, useState, useContext, useEffect, useMemo, useRef } from 'react';
import { GoogleMap, Marker, MarkerClusterer } from '@react-google-maps/api';
import { useLazyQuery, gql } from '@apollo/client';

import styled, { css, ThemeContext } from 'styled-components';
import haversine from 'haversine-distance';

import LocationContext from './Context';
import Button from './Button';
import MapAside from './MapAside';
// import SmallMarker from './SmallMarker';
// import CustomMarker from './CustomMarker';
import MarkIcon from '../assets/icons/pin-blue.svg';
import MarkIconGC from '../assets/icons/pin-green.svg';
import Loader from './Loader';
import { useGeolocation, useQueryString, useWindowSize } from '../hooks';
// import Error from './Error';

const defaultCenter = {
    lat: 52.614838,
    lng: -1.2076397,
};

const clusterOptions = {
    minimumClusterSize: 10,
    imagePath: 'https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m', // m1.png, m2.png, m3.png, m4.png and m5.png  in that folder
};

const StyledMapPage = styled.div`
    position: relative;
    overflow: hidden;
    flex-grow: 1;
`;

const StyledAreaChange = styled.div`
    ${({ theme }) => css`
        position: absolute;
        left: 50%;
        top: 110px;
        transform: translateX(-50%);
        z-index: 90;
        @media (min-width: ${theme.breakpoints.sm}px) {
            top: 30px;
        }
    `}
`;

const GET_EXTRA_BRAND = gql`
    query GetDataBrand($brand: ID!) {
        brand(id: $brand, idType: DATABASE_ID) {
            name
            databaseId
        }
    }
`;
const GET_EXTRA_PRODUCT = gql`
    query GetDataProduct($product: ID!) {
        product(id: $product, idType: DATABASE_ID) {
            databaseId
            title
        }
    }
`;

const GET_MARKERS = gql`
    query GetMarkers($lat: Float!, $lng: Float!, $brand: ID, $product: ID, $type: StockistTypeEnum) {
        stockists(
            where: {
                stockistType: $type
                location: { lat: $lat, lng: $lng }
                product: { brand: $brand, product: $product }
                status: PUBLISH
            }
            first: 500
        ) {
            edges {
                node {
                    id
                    databaseId
                    title
                    stockistMeta {
                        stockistId
                        stockistType
                        stockistAddress
                        stockistAddress2
                        stockistAddress3
                        stockistCounty
                        stockistPostcode
                        stockistTelephoneNumber
                        stockistTown
                        stockistLocation {
                            latitude
                            longitude
                        }
                    }
                }
                distance(lat: $lat, lng: $lng)
            }
        }
    }
`;

const deDup = (items) => {
    if (!items) {
        return [];
    }
    const seen = new Set();

    const stockists = items.filter((el) => {
        const duplicate = seen.has(el.node.stockistMeta.stockistId);
        seen.add(el.node.stockistMeta.stockistId);
        return !duplicate;
    });

    return stockists;
};

export const Map = () => {
    const {
        selectedLatLng,
        setSelectedLatLng,
        filters,
        setFilters,
        setSearchType,
        selectedMarker,
        setSelectedMarker,
        siteSettings,
        appHeight,
    } = useContext(LocationContext);
    const { colors, typography } = useContext(ThemeContext);
    const params = useQueryString();

    const [map, setMap] = useState<any>(null);
    const [showSearchThisAreaButton, setShowSearchThisAreaButton] = useState(false);

    const [currentCenter, setCurrentCenter] = useState<any>(selectedLatLng);
    const [showSearch, setShowSearch] = useState(true);
    const [showFilters, setShowFilters] = useState(false);
    const [triedGeo, setTriedGeo] = useState(false);
    const [containerStyle, setContainerStyle] = useState({
        width: '100%',
        height: '430px',
    });
    const { width } = useWindowSize();
    const { location, getLocation, locationError } = useGeolocation(false);
    // Ref for virtual list
    const listRef = useRef<any>(null);

    const [loadMarkers, { called, loading, error, data }] = useLazyQuery(GET_MARKERS);
    const [
        loadExtraBrand,
        { called: extraCalled, loading: extraLoading, error: extraError, data: extraData },
    ] = useLazyQuery(GET_EXTRA_BRAND);
    const [
        loadExtraProduct,
        { called: extraCalledProduct, loading: extraLoadingProduct, error: extraErrorProduct, data: extraDataProduct },
    ] = useLazyQuery(GET_EXTRA_PRODUCT);

    const stockists = useMemo(() => deDup(data?.stockists?.edges), [data]);

    useEffect(() => {
        // We already have filters so don't need todo anything
        if (extraCalled || filters?.product?.value || filters?.brand?.value) {
            return;
        }

        const productID = params.get('productID');
        const brandID = params.get('brandID');

        if (brandID) {
            loadExtraBrand({
                variables: {
                    brand: brandID,
                },
            });
        }
        if (productID) {
            loadExtraProduct({
                variables: {
                    product: productID,
                },
            });
        }
    }, [params, filters]);

    useEffect(() => {
        if (filters?.product?.value || filters?.brand?.value) {
            return;
        }
        if (extraCalled && !extraLoading && !extraError && extraData) {
            const newFilters: any = {};

            if (extraData?.brand?.name) {
                newFilters.brand = { value: extraData?.brand?.databaseId, label: extraData?.brand?.name };
            }
            if (extraData?.product?.title) {
                newFilters.product = { value: extraData?.product?.databaseId, label: extraData?.product?.title };
            }

            setFilters((f) => ({
                ...f,
                ...newFilters,
            }));
        }
    }, [extraCalled, extraLoading, extraError, extraData]);

    useEffect(() => {
        let { height } = containerStyle;
        const checkHeight = width && width >= 768 ? 860 : 430;

        if (appHeight && appHeight >= checkHeight) {
            const bannerElm = document.querySelector('.stockist-banner-msg') as HTMLElement;
            let newHeight;
            if (bannerElm) {
                newHeight = appHeight - bannerElm.offsetHeight;
            } else {
                newHeight = appHeight;
            }

            if (width && width < 768) {
                height = `${newHeight - 248}px`;
            } else {
                height = `${newHeight}px`;
            }
        } else {
            height = `${checkHeight}px`;
        }

        setContainerStyle((s) => ({
            ...s,
            height,
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appHeight]);

    const onLoad = useCallback(function callback(gmap) {
        // const bounds = new window.google.maps.LatLngBounds();
        // gmap.fitBounds(bounds);
        setMap(gmap);
    }, []);

    const onUnmount = useCallback(function callback() {
        setMap(null);
    }, []);

    // Center the map on selected Latlng
    useEffect(() => {
        if (selectedLatLng) {
            setCurrentCenter(selectedLatLng);
        }
        if (!selectedLatLng && !triedGeo) {
            getLocation();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedLatLng]);

    // If we can get the users location and we dont already have one
    // Or if we get loc from params
    useEffect(() => {
        const lat = params.get('lat');
        const lng = params.get('lng');

        if (lat && lng) {
            const latLng = {
                lat: +lat,
                lng: +lng,
            };

            setSelectedLatLng(latLng);

            if (params.get('searchType')) {
                setSearchType(params.get('searchType'));
            }
        } else if (location?.latitude && !selectedLatLng && !locationError) {
            setSelectedLatLng({ lat: location.latitude, lng: location.longitude });
            setSearchType('GEO');
        } else if (locationError?.code) {
            // TODO handle this error better
            //
            setCurrentCenter(defaultCenter);
        }
        // Only run once
        setTriedGeo(true);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location, locationError]);

    useEffect(() => {
        if (selectedMarker) {
            const item = stockists.find((stock) => stock.node.id === selectedMarker);
            if (item) {
                const { latitude: lat, longitude: lng } = item.node.stockistMeta.stockistLocation;
                map.panTo({ lat, lng });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedMarker]);

    useEffect(() => {
        if (!currentCenter) {
            return;
        }
        const variables: any = {
            lat: currentCenter.lat,
            lng: currentCenter.lng,
        };

        if (filters?.brand?.value) {
            variables.brand = filters.brand.value;
        }
        if (filters?.product?.value) {
            variables.product = filters.product.value;
        }
        if (filters?.stockistType) {
            variables.type = filters.stockistType;
        } else {
            variables.type = 'ANY';
        }
        loadMarkers({
            variables,
        });
        setShowSearchThisAreaButton(false);
    }, [currentCenter, filters, loadMarkers]);

    const calculateMapMovedDistance = (previousCenter, newCentre) => {
        const distance = haversine(previousCenter, newCentre);
        const miles = distance * 0.00062137;

        // TODO change  magic Search numbers
        const range = siteSettings?.stockistRange || 10;
        if (miles >= range) {
            setShowSearchThisAreaButton(true);
        }
    };

    const handleCenterChanged = () => {
        if (map) {
            const newCenterPos = map.getCenter();
            const newCenter = {
                lat: newCenterPos.lat(),
                lng: newCenterPos.lng(),
            };

            calculateMapMovedDistance(currentCenter, newCenter);
        }
    };

    const handleChangeCenter = () => {
        const newCenterPos = map.getCenter();
        const newCenter = {
            lat: newCenterPos.lat(),
            lng: newCenterPos.lng(),
        };

        setCurrentCenter(newCenter);
        setSearchType('AREA');

        const filterItems: string[] = [];

        if (filters?.stockistType === 'GARDEN_CENTRE') {
            filterItems.push('garden centres only');
        }
        if (filters?.brand?.label) {
            filterItems.push(filters.brand.label);
        }
        if (filters?.product?.label) {
            filterItems.push(filters.product.label);
        }

        // NEW TRACKING
        window?.dataLayer?.push({
            event: 'find_a_stockist',
            action: 'search new area',
            page_path: '/stockists/map',
            filters: filterItems.length >= 1 ? filterItems.join(' - ') : '',
        });
    };

    const handleShowStockist = (id, index) => {
        listRef.current.scrollToIndex({
            index,
            align: 'start',
        });
        setShowSearch(true);
        setShowFilters(false);
        setSelectedMarker(id);
    };

    const showData = !loading && !error && called && stockists && stockists.length >= 1;
    return (
        <>
            {/* {locationError && <Error error="Error Getting Location" />} */}
            <StyledMapPage>
                {showSearchThisAreaButton && !error && (
                    <StyledAreaChange>
                        <Button label="SEARCH THIS AREA" onClick={handleChangeCenter} />
                    </StyledAreaChange>
                )}

                <GoogleMap
                    mapContainerStyle={containerStyle}
                    center={currentCenter}
                    zoom={12}
                    onLoad={onLoad}
                    onUnmount={onUnmount}
                    onCenterChanged={handleCenterChanged}
                    options={{ mapTypeControl: false, fullscreenControl: false }}
                >
                    {/* Child components, such as markers, info windows, etc. */}

                    {showData && (
                        <MarkerClusterer options={clusterOptions}>
                            {(clusterer) =>
                                stockists.map(({ node }, index) => {
                                    const GC = node?.stockistMeta?.stockistType === 'GARDEN_CENTRE';
                                    const selected = selectedMarker === node.id;
                                    const size = selected ? new google.maps.Size(58, 75) : new google.maps.Size(42, 54);
                                    const fontSize = selected ? '22px' : '16px';
                                    const url = GC ? MarkIconGC : MarkIcon;
                                    const color = GC ? colors.brand : colors.alt;

                                    const labelOrigin = selected
                                        ? new google.maps.Point(29, 29)
                                        : new google.maps.Point(21, 21);
                                    return (
                                        <Marker
                                            key={node.id}
                                            position={{
                                                lat: node.stockistMeta.stockistLocation.latitude,
                                                lng: node.stockistMeta.stockistLocation.longitude,
                                            }}
                                            icon={{
                                                url,
                                                size,
                                                scaledSize: size,
                                                labelOrigin,
                                            }}
                                            zIndex={selected ? stockists.length + 2 : index + 1}
                                            label={{
                                                text: `${index + 1}`,
                                                color,
                                                fontFamily: typography.fontFamily,
                                                fontSize,
                                            }}
                                            onClick={() => handleShowStockist(node.id, index)}
                                            clusterer={clusterer}
                                        />
                                    );
                                })
                            }
                        </MarkerClusterer>
                    )}
                </GoogleMap>
                <MapAside
                    showData={showData}
                    stockists={stockists}
                    listRef={listRef}
                    showSearch={showSearch}
                    setShowSearch={setShowSearch}
                    showFilters={showFilters}
                    setShowFilters={setShowFilters}
                    loading={loading}
                    called={called}
                />
                <Loader loading={loading && !error} />
            </StyledMapPage>
        </>
    );
};
